import { UseFormSetError } from 'react-hook-form';

import { CustomFieldKeys, EndType, WidgetInputs } from '../@types/types';
import { GiftType, OnetimeGiving, PCGLQueryKeys, dateFieldOptions, embeddedSource } from '../shared/constants';
import { generateMultiFundParams, getLineItemsArray } from '../shared/utils';
import dayjs from '../utils/dayjs-config';

export function useFormSubmit(
	handle: string,
	customFieldKeys: CustomFieldKeys = {},
	setError: UseFormSetError<WidgetInputs & any>,
	afterSubmitHandler?: (args: any) => void
) {
	return async (data: WidgetInputs & { [key: string]: string }) => {
		try {
			if (!handle) {
				throw new Error('Invalid Handle');
			}

			const url = APP_SETTINGS.webGivingUrl;
			const inIframe = window.self !== window.top;
			const isRecurring = data.GiftType === GiftType.RECURRING.toString();

			const multifunds = getLineItemsArray(data.MultiFunds);

			const PCGL = {
				[PCGLQueryKeys.Source]: embeddedSource,
				[PCGLQueryKeys.Amount]: data.Amount,
				[PCGLQueryKeys.FundKeyOrName]: data.FundKeyOrName,
				[PCGLQueryKeys.AttemptSkipGivePage]: true,
			};

			if (data.OpenIdConnectProvider) {
				PCGL[PCGLQueryKeys.OpenIdConnectProvider] = data.OpenIdConnectProvider;
			}

			const customKeys = Object.keys(customFieldKeys);
			if (customKeys.length) {
				customKeys.forEach(key => {
					if (!data[key]) return;
					PCGL[customFieldKeys[key]] = encodeURIComponent(data[key]);
				});
			}

			if (multifunds.length) {
				multifunds.forEach(({ Amount, FundKeyOrName }, i) => {
					const param = generateMultiFundParams(i);
					PCGL[param.amount] = encodeURIComponent(Amount);
					PCGL[param.fund] = encodeURIComponent(FundKeyOrName);
				});
				delete PCGL[PCGLQueryKeys.Amount];
				delete PCGL[PCGLQueryKeys.FundKeyOrName];
			}

			if (window.pushpayEmbeddedConfig && window.pushpayEmbeddedConfig.wgc) {
				PCGL[PCGLQueryKeys.SignedConfig] = window.pushpayEmbeddedConfig.wgc;
			}

			if (data.Recurrence) {
				PCGL[PCGLQueryKeys.Recurrence] = data.Recurrence;
			} else {
				throw new Error('Invalid Recurrence');
			}

			if (!isRecurring) {
				if (data.GiftDate && dayjs(data.GiftDate).startOf('day').isBefore(dayjs().startOf('day'))) {
					setError('GiftDate', { type: 'manual', message: 'errors.dateInPast' });
					return;
				}
				PCGL[PCGLQueryKeys.GiftDate] = data.GiftDate;
				PCGL[PCGLQueryKeys.Recurrence] = OnetimeGiving;
			} else {
				if (
					data.RecurringStartDate &&
					dayjs(data.RecurringStartDate).startOf('day').isBefore(dayjs().startOf('day'))
				) {
					setError('RecurringStartDate', { type: 'manual', message: 'errors.dateInPast' });
					return;
				}

				PCGL[PCGLQueryKeys.RecurringStartDate] = data.RecurringStartDate;

				if (
					data.RecurringEndType === EndType.Date.toString() &&
					data.RecurringStartDate &&
					dayjs(data.RecurringEndDate).startOf('day').isBefore(dayjs(dateFieldOptions.minEnd).startOf('day'))
				) {
					setError('RecurringEndDate', { type: 'manual', message: 'errors.endAfterStart' });
					return;
				}

				PCGL[PCGLQueryKeys.RecurringEndType] = data.RecurringEndType;
				PCGL[PCGLQueryKeys.RecurringEndDate] = data.RecurringEndDate;
				PCGL[PCGLQueryKeys.RecurringEndOccurence] = data.RecurringEndOccurence;
			}

			const params = Object.keys(PCGL).reduce((acc, key) => {
				const value = PCGL[key];
				if (value) {
					acc.push(`${key}=${value}`);
				}
				return acc;
			}, [] as string[]);

			if (afterSubmitHandler) {
				afterSubmitHandler({
					handle,
					...PCGL,
				});
			} else {
				const redirectUrl = `${url}${handle}?${params.join('&')}`;
				if (inIframe && window.top) {
					window.top.location.href = redirectUrl;
					return;
				}
				window.location.href = redirectUrl;
			}
		} catch (error) {
			// TODO: Add error handling
			window.console.error(error);
		}
	};
}
